'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { NewsFeedSection } from '@/types/sanity';
import { SectionHeader } from './SectionHeader';
import { SectionCTAs } from './SectionCTAs';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import {
  ScrollReveal,
  StaggerContainer,
  staggerItemVariants,
} from '@/components/animations/ScrollAnimations';
import { urlFor } from '@/sanity/lib/image';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/utils/date';
import { Play } from 'lucide-react';

interface NewsFeedSectionComponentProps {
  section: NewsFeedSection;
  pageSlug?: string;
}

export function NewsFeedSectionComponent({ section, pageSlug }: NewsFeedSectionComponentProps) {
  const displayLimit = section.displayLimit || section.articles?.length || 0;
  const articlesToShow = section.articles?.slice(0, displayLimit) || [];

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'news-feed-section py-8 md:py-12 lg:py-16',
          pageSlug && `news-feed-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <SectionHeader
              title={section.title}
              subtitle={section.subtitle}
              description={section.description}
            />

            <StaggerContainer
              staggerDelay={0.12}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 max-w-6xl mx-auto"
            >
              {articlesToShow.map((article) => (
                <motion.div key={article._id} variants={staggerItemVariants}>
                  <Link href={`/news/${article.slug.current}`} className="group block">
                    <div className="aspect-video relative rounded-xl overflow-hidden bg-muted mb-3">
                      {article.image?.asset ? (
                        <Image
                          src={urlFor(article.image).width(640).height(360).quality(80).url()}
                          alt={article.image?.alt || article.title}
                          fill
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                          No image
                        </div>
                      )}
                      {article.videoUrl && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90">
                            <Play className="h-5 w-5 text-foreground fill-foreground ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>
                    <h3 className="font-medium text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {article.author && <span>{article.author}</span>}
                      {article.author && article.publishedAt && <span> · </span>}
                      {article.publishedAt && (
                        <time dateTime={article.publishedAt}>
                          {formatTimeAgo(article.publishedAt)}
                        </time>
                      )}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </StaggerContainer>

            {section.showLoadMore && articlesToShow.length < (section.articles?.length || 0) && (
              <div className="text-center mt-8">
                <Button variant="outline">Load More</Button>
              </div>
            )}

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
